import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Play, Pause, ChevronRight, User, Cpu, Workflow, Gauge, Coins, Radio } from "lucide-react";
import { useCases as MOCK_CASES } from "@/data/mockData";
import { useWorkspace } from "@/context/WorkspaceContext";
import StatusPill from "@/components/aieco/StatusPill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NODES = [
  { id: "applicant", label: "Applicant Agent", icon: User },
  { id: "credit", label: "Credit Agent", icon: Cpu },
  { id: "decision", label: "Decision Agent", icon: Workflow },
  { id: "outcome", label: "Outcome", icon: Gauge },
];

const STATUS_COLOR = {
  success: "#22C55E",
  warning: "#F59E0B",
  critical: "#EF4444",
  idle: "#5B8DEF",
};
const STATUS_PULSE = { success: "pulse-success", warning: "pulse-warning", critical: "pulse-critical" };

function fmtMoney(n) {
  return `$${n.toLocaleString()}`;
}

function WorkflowDiagram({ uc, activeIdx }) {
  return (
    <div className="card-eco relative p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Live Workflow</div>
          <div className="mt-1 font-display text-lg text-slate-100">
            Applicant → Credit → Decision → Outcome
          </div>
        </div>
        <StatusPill tone={uc.status === "success" ? "success" : uc.status === "warning" ? "warning" : "critical"}>
          {uc.outcome}
        </StatusPill>
      </div>

      <div className="relative grid grid-cols-7 items-center gap-2 py-6">
        {NODES.map((n, i) => {
          const isPast = i < activeIdx;
          const isActive = i === activeIdx;
          const step = uc.steps[i];
          const tone = step?.status || "idle";
          const color = STATUS_COLOR[tone];

          return (
            <React.Fragment key={n.id}>
              <motion.div
                initial={false}
                animate={{ scale: isActive ? 1.04 : 1, opacity: isPast || isActive ? 1 : 0.55 }}
                transition={{ type: "spring", stiffness: 240, damping: 22 }}
                className="col-span-1"
              >
                <div
                  className={`relative rounded-xl border p-3 ${isActive ? STATUS_PULSE[tone] || "" : ""}`}
                  style={{
                    borderColor: isPast || isActive ? `${color}55` : "rgba(255,255,255,0.06)",
                    background: isPast || isActive ? `linear-gradient(180deg, ${color}14, ${color}05)` : "rgba(255,255,255,0.02)",
                  }}
                  data-testid={`workflow-node-${n.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="flex h-7 w-7 items-center justify-center rounded-lg"
                      style={{ background: `${color}1F`, color }}
                    >
                      <n.icon size={14} strokeWidth={1.7} />
                    </div>
                    <div className="text-[11px] font-medium text-slate-100">{n.label}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{
                        background: isPast || isActive ? color : "#475569",
                        boxShadow: isPast || isActive ? `0 0 10px ${color}` : "none",
                      }}
                    />
                    <span className="font-mono text-[10px] text-slate-400">
                      {isPast || isActive ? (step?.status || "ok").toUpperCase() : "QUEUED"}
                    </span>
                  </div>
                </div>
              </motion.div>

              {i < NODES.length - 1 && (
                <div className="col-span-1 h-px w-full">
                  <svg width="100%" height="20" viewBox="0 0 100 20" preserveAspectRatio="none">
                    <line
                      x1="0"
                      y1="10"
                      x2="100"
                      y2="10"
                      stroke={isPast ? STATUS_COLOR[uc.steps[i]?.status || "idle"] : "rgba(255,255,255,0.10)"}
                      strokeWidth="1.5"
                      className={isPast ? "edge-flow" : ""}
                    />
                    <polygon
                      points="96,6 100,10 96,14"
                      fill={isPast ? STATUS_COLOR[uc.steps[i]?.status || "idle"] : "rgba(255,255,255,0.20)"}
                    />
                  </svg>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 border-t border-white/5 pt-4 md:grid-cols-3">
        <div className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] p-3">
          <img src={uc.avatar} alt={uc.name} className="h-10 w-10 rounded-full object-cover" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Applicant</div>
            <div className="text-sm text-slate-100">{uc.name}</div>
          </div>
        </div>
        <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Income / Requested</div>
          <div className="mt-1 font-mono text-sm text-slate-100">
            {fmtMoney(uc.income)} <span className="text-slate-600">/</span> {fmtMoney(uc.requested)}
          </div>
        </div>
        <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Risk · Tokens</div>
          <div className="mt-1 font-mono text-sm" style={{ color: STATUS_COLOR[uc.status] }}>
            {uc.riskScore} <span className="text-slate-600">·</span> <span className="text-slate-200">{uc.tokens.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventStream({ uc, activeIdx }) {
  return (
    <div className="card-eco p-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Runtime events</div>
      <div className="mt-3 space-y-2">
        <AnimatePresence initial={false}>
          {uc.steps.slice(0, activeIdx + 1).map((s, i) => {
            const color = STATUS_COLOR[s.status];
            return (
              <motion.div
                key={`${uc.id}-${i}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-start gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5"
              >
                <span className="mt-1 h-1.5 w-1.5 rounded-full" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[12px] text-slate-200">{s.agent}</div>
                    <div className="font-mono text-[10px] text-slate-500">{s.time}</div>
                  </div>
                  <div className="text-[11px] text-slate-400">{s.action}</div>
                  <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-slate-500">
                    <span>risk {s.risk}</span>
                    <span>·</span>
                    <span>{s.tokens.toLocaleString()} tok</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

function PolicyChecks({ uc, activeIdx }) {
  const lines = [
    { ok: true, text: "Profile fields within allowed scope" },
    { ok: uc.id !== "priya", text: "No blocked fields accessed" },
    { ok: uc.id !== "james", text: "Approval ≤ 3× verified income" },
    { ok: uc.riskScore <= 60, text: "Risk score ≤ Auto-execute threshold (60)" },
  ];
  return (
    <div className="card-eco p-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Policy checks</div>
      <ul className="mt-3 space-y-2 text-[12px]">
        {lines.map((l, i) => (
          <li key={i} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
            <span className="text-slate-300">{l.text}</span>
            <span
              className="rounded-full px-2 py-0.5 font-mono text-[10px]"
              style={{
                color: l.ok ? "#22C55E" : "#EF4444",
                background: l.ok ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)",
                border: `1px solid ${l.ok ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
              }}
            >
              {l.ok ? "PASS" : "BLOCK"}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 rounded-lg border border-white/5 bg-white/[0.02] p-3 text-[12px] text-slate-300">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Outcome</div>
        <div className="mt-1">{uc.policyResult}</div>
        <div className="mt-1 text-[11px] text-slate-500">{uc.summary}</div>
      </div>
    </div>
  );
}

function ThresholdBar({ riskScore }) {
  return (
    <div className="card-eco p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Human thresholds</div>
        <span className="font-mono text-[11px] text-slate-300">risk = {riskScore}</span>
      </div>
      <div className="relative mt-4 h-2 w-full overflow-hidden rounded-full">
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#22C55E 0%,#22C55E 60%,#F59E0B 60%,#F59E0B 80%,#EF4444 80%)" }} />
        <motion.div
          initial={{ left: "0%" }}
          animate={{ left: `calc(${riskScore}% - 6px)` }}
          transition={{ duration: 0.6 }}
          className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-[#0B1020] bg-white shadow"
        />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[0.16em] text-slate-500">
        <div>0–60 Auto execute</div>
        <div className="text-center">61–80 Review</div>
        <div className="text-right">81–100 Block</div>
      </div>
    </div>
  );
}

export default function RuntimeMonitor() {
  const { t } = useTranslation();
  const { workspaceId } = useWorkspace();
  const [backendDriven, setBackendDriven] = useState(false);
  const [idx, setIdx] = useState(0);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [holdLeft, setHoldLeft] = useState(0);

  // Source-of-truth for case meta (id/name/income/etc) — backend, with mock fallback.
  // We always reuse the rich step+avatar metadata from mock data to render animation,
  // overriding the basic fields backend supplies.
  const useCases = MOCK_CASES;

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/runtime/cases`, { params: { workspace: workspaceId } }).then((r) => {
      if (!cancelled && r.data?.cases?.length) setBackendDriven(true);
    }).catch(() => { if (!cancelled) setBackendDriven(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const uc = useCases[idx];
  const STEP_MS = 1000;
  const HOLD_MS = 5000;
  const atEnd = step >= uc.steps.length - 1;

  useEffect(() => {
    if (!playing) return;
    if (step < uc.steps.length - 1) {
      const t = setTimeout(() => setStep((s) => s + 1), STEP_MS);
      return () => clearTimeout(t);
    } else {
      const start = Date.now();
      setHoldLeft(Math.ceil(HOLD_MS / 1000));
      const ticker = setInterval(() => {
        const left = Math.ceil((HOLD_MS - (Date.now() - start)) / 1000);
        setHoldLeft(Math.max(0, left));
      }, 250);
      const t = setTimeout(() => {
        setIdx((i) => (i + 1) % useCases.length);
        setStep(0);
      }, HOLD_MS);
      return () => {
        clearTimeout(t);
        clearInterval(ticker);
      };
    }
  }, [step, idx, playing, uc.steps.length]);

  const selectCase = (i) => {
    setIdx(i);
    setStep(0);
  };

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-runtime">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
            {t("runtime.subtitle")}
            {backendDriven && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] normal-case tracking-normal text-emerald-300" data-testid="backend-driven-badge">
                <Radio size={10} aria-hidden="true" /> {t("runtime.backendDriven")}
              </span>
            )}
          </div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">
            {t("runtime.title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            {t("runtime.lede")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {useCases.map((u, i) => (
            <button
              key={u.id}
              onClick={() => selectCase(i)}
              data-testid={`case-tab-${u.id}`}
              className={`rounded-lg border px-3 py-1.5 text-[12px] transition ${
                i === idx
                  ? "border-[#5B8DEF]/50 bg-[#5B8DEF]/10 text-[#cfe0ff]"
                  : "border-white/5 bg-white/[0.02] text-slate-400 hover:text-slate-200"
              }`}
            >
              {u.name}
            </button>
          ))}
          <button
            onClick={() => setPlaying((p) => !p)}
            data-testid="play-pause"
            className="flex items-center gap-1.5 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[12px] text-slate-200 hover:border-white/10"
          >
            {playing ? <Pause size={12} /> : <Play size={12} />}
            {playing ? "Pause" : "Play"}
          </button>
        </div>
      </motion.div>

      {/* progress + workflow */}
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between gap-3 text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <span>Case {idx + 1} of {useCases.length}</span>
            <span>·</span>
            <span>Auto-cycling demo</span>
          </div>
          {playing && atEnd && (
            <div className="flex items-center gap-2 rounded-full border border-[#5B8DEF]/30 bg-[#5B8DEF]/[0.06] px-3 py-1 font-mono text-[11px] text-[#cfe0ff]" data-testid="case-countdown">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#5B8DEF] opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#5B8DEF]" />
              </span>
              Next case in {holdLeft}s
            </div>
          )}
        </div>
        <WorkflowDiagram uc={uc} activeIdx={step} />
      </div>

      <div className="mt-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-7">
          <EventStream uc={uc} activeIdx={step} />
        </div>
        <div className="col-span-12 space-y-6 xl:col-span-5">
          <PolicyChecks uc={uc} activeIdx={step} />
          <ThresholdBar riskScore={uc.riskScore} />
        </div>
      </div>
    </div>
  );
}
